<template>
	<main class="page">
		<div class="subsida">

			<h1 class="title nooutline" tabindex="-1" ref="svaraHeader" v-if="visaKvittenssida()">Tack för ditt svar!</h1>
			<h1 class="title nooutline" tabindex="-1" ref="svaraHeader" v-else>{{ $t("main.title")}}</h1>

			<div class="company">
				<div class="company_info">
					<p class="company_info_name">{{ foretagsnamn }}</p>
					<p class="company_info_number" aria-hidden="true">{{ organisationsnummer }}</p>
					<p class="sr-only">Organisationsnummer, {{ a11yorganisationsnummer }}</p>
				</div>
				<div class="icon">
					<img class="icon_img" src="../../public/images/start_building-round.svg" alt="" aria-hidden="true">
				</div>
			</div>

			<div aria-live="polite" aria-atomic="false">
				<div v-if="visaKvittenssida()" class="content">
					
					<div class="errormsg" tabindex="-1" role="alert"
						v-if="visaFelmeddelande()">
						<img class="errormsg_symbol" width="32" height="32"
							src="../../public/images/error.svg" alt="" />
						<p>{{store.state.felmeddelande}}</p>
					</div>	

					<div class="kvittens-kvitto">
						<div class="icon-left">
							<img
							class="check-icon"
							src="../../public/images/check.svg"
							alt=""
							/>
						</div>
						<div class="div-positioner">
							<div class="text">
								<p aria-hidden="true">
									Bolagsverket har tagit emot ditt svar för <strong>{{ foretagsnamn }}</strong>, {{ organisationsnummer }}.
								</p>
								<p class="sr-only">
									Bolagsverket har tagit emot ditt svar för {{ foretagsnamn }}, organisationsnummer {{ a11yorganisationsnummer }}.
								</p>
							</div>
							<div class="kvittens-container">
								
								<dl>
									<dt>Datum:</dt>
									<dd>{{ skapaDatumtext() }}</dd>				
									<dt aria-hidden="true">Kvittensnummer:</dt>
									<dd aria-hidden="true">{{ store.state.kvittensnummer }}</dd>
									<dt class="sr-only">Kvittensnummer:</dt>
									<dd class="sr-only">{{ skapaA11yKvittensnummer() }}</dd>
								</dl>
							</div>
						</div>
					</div>

				</div>
				<div v-else class="content" id="svaraanvy">
					
					<div class="errormsg" tabindex="-1" role="alert"
						v-if="visaFelmeddelande()">
						<img class="errormsg_symbol" width="32" height="32"
							src="../../public/images/error.svg" alt="" />
						<p>{{store.state.felmeddelande}}</p>
					</div>
					
					<div v-if="visaLaddarSymbol()" class="laddar-container">
						<LaddarSymbol/>
					</div>
					<div v-else>
						<Form @submit="onSubmit" :validation-schema="schema"
							@invalid-submit="onInvalidSubmit" v-slot="{ errors }">

							<div class="errormsg" role="alert"
								v-if="Object.keys(errors).length != 0">
								<img class="errormsg_symbol" width="32" height="32"
									src="../../public/images/error.svg" alt="" />
								<div class="errormsg_content">
									<h2 tabindex="-1">
										<template v-if="Object.keys(errors).length == 1">{{ $t("general.errors.correct_issue") }}</template>
										<template v-else>{{ $t("general.errors.correct_issues", { count: Object.keys(errors).length.toString()}) }}</template>
									</h2>
									<ul>
										<li v-if="errors.stakvar"><a class="globalMessage" href="#stakvar">{{ errors.stakvar }}</a></li>
										<li v-if="errors.email"><a class="globalMessage" href="#email">{{ errors.email }}</a></li>
										<li v-if="errors.confirm_email"><a class="globalMessage" href="#confirm_email">{{ errors.confirm_email }}</a></li>
									</ul>
								</div>
							</div>
							<div class="form-container">
								<div class="form-row">
									<CustomCheckbox 
										id="stakvar" 
										name="stakvar" 
										:required="true"
										mode="passive"
										:label="checkboxText" 
										class="light"
										:arialabel="'Ja, ' + checkboxText"
									/>
								</div>
								<div class="form-row">
									<h2>{{ $t("general.info_texts.enter_your_email_address_title") }}</h2>
									<p>{{ $t("general.info_texts.enter_your_email_address_text") }}</p>
									<div class="form-row">
										<TextInput 
											id="email" 
											name="email" 
											type="email" 
											:required="false"
											mode="passive"
											label="E-postadress (valfri)"
											autocomplete="on"
											class="text-black light"
											:required-pattern="epostRegex"
											default-value=""
											/>
									</div>
									<div class="form-row">
										<TextInput 
											id="confirm_email" 
											name="confirm_email" 
											type="email"
											mode="passive"
											:required="false"
											class="text-black light"
											label="Bekräfta e-postadress" 
											arialabel="Bekräfta den epostadress du skrev i fältet innan." 
											autocomplete="new-password"
											:required-pattern="epostRegex"
											default-value=""
											/>
									</div>
								</div>
							</div>
							<div class="button-container">
								<button aria-label="Skicka in" class="next"
									type="submit">Skicka in</button>
							</div>
						</Form>
					</div>
				</div>
			</div>
		</div>
	</main>
</template>

<script setup lang="ts">
import TextInput from '@/components/partials/TextInput.vue';
import CustomCheckbox from '@/components/partials/CustomCheckbox.vue';
import LaddarSymbol from '@/components/partials/LaddarSymbol.vue';

import { ref, onMounted, nextTick } from 'vue'
import { Form } from 'vee-validate';
import * as Yup from 'yup';
import { toUnicode } from 'punycode';

import { useStore } from '@/store';
import { verifieraOchBekrafta } from '@/api';
import { separeraTecken } from "@/format";
import { epostRegex } from '@/validation'
import router from '@/router';

const store = useStore();

const skapaOrgnummertext = (): string => {
	const orgnummer: string = store.state.orgnummerKomplett;
	const idx: number = orgnummer.length - 4;
	return `${orgnummer.slice(0, idx)}-${orgnummer.slice(idx)}`;
}

const skapaA11yKvittensnummer = () : string => {
	return separeraTecken(store.state.kvittensnummer, " ");
}

const zeroPad = (n: number): string => {
	return String(n).padStart(2, "0");
}

const skapaDatumtext = (): string => {
	// Ankomsttid är angivet i sekunder, omvandla till millisekunder
	const datum: Date = new Date(store.state.ankomsttid * 1000);
	const timme: string = zeroPad(datum.getHours());
	const minut: string = zeroPad(datum.getMinutes());
	return `${datum.toLocaleDateString("sv-SV")}, kl. ${timme}.${minut}`;
}

const visaKvittenssida = (): boolean => {
	return store.state.kvittensnummer.length > 0;
}

const visaLaddarSymbol = (): boolean => {
	return store.state.vantarPaResponse;
}

const visaFelmeddelande = (): boolean => {
	return store.state.felmeddelande.length > 0;
}


/* Se till att vi alltid visa något (under utvecklingsfasen) */
const foretagsnamn: string = store.state.foretagsnamn.length > 0 ? store.state.foretagsnamn : "FÖRETAGSNAMN AB";
const organisationsnummer: string = store.state.orgnummerKomplett.length > 0 ? skapaOrgnummertext() : "556677-8899";
const a11yorganisationsnummer: string = separeraTecken(organisationsnummer, " "); // För uppläsning (X X X X X X - X X X X)

import i18n from "../i18n";
const benamning: string = store.state.arForetag ? i18n.global.t("general.the_company") : i18n.global.t("general.the_association");
const checkboxText = i18n.global.t("general.info_texts.remain_in_the_register", { name: benamning });
const epostvalidera: string = i18n.global.t("general.errors.invalid_email");
const repeteraepostvalidera: string = i18n.global.t("general.errors.confirm_email");
const markeraStakvar: string = i18n.global.t("general.errors.confirm_checkbox", { name: benamning.toLowerCase() });

const schema = Yup.object().shape({
	email: Yup
		.string()
		.matches(new RegExp(epostRegex), epostvalidera),
	confirm_email: Yup
		.string()
		.matches(new RegExp(epostRegex), epostvalidera)
		.oneOf([Yup.ref('email')], repeteraepostvalidera),
	stakvar: Yup
		.bool()
        .required(markeraStakvar),
});

const onSubmit = () => {
	// Hantera åäö i epostadress. Vissa webbläsare (Chrome & Edge) encodar non-ASCII till ASCII (punycode).
	// Alternativt måste <input type="email"> bytas till <input type="text">.
	const epost = toUnicode(email.value.trim()); 
	store.state.epost = epost.length > 0 ? epost : null;
	store.state.staKvar = stakvar.checked;
	verifieraOchBekrafta(() => {
		router.push("/kvittens");
	});
};

const onInvalidSubmit = () => {
	const firstEl = document.querySelector('[aria-invalid="true"]');
	if (firstEl) {
		firstEl.focus();
	}	
};

//const filter = ref(null);
const svaraHeader = ref(null)

onMounted(() => {
    nextTick(() => {
		svaraHeader.value.focus();
		console.log(document.activeElement);
    });
})

</script>

<style lang="scss" scoped>
.laddar-container {
	display: grid;
  	place-items: center;
  	height: 10rem;
	z-index: 10;
}
/*.laddar-container::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: white;
    pointer-events: all;
    opacity: .05;
}*/
.flex {
	display: flex;
	flex-direction: row;
}
main {
	max-width: 40em;
	margin-top: 0;
	width: 100%;
	display: flex;
}
.nooutline {
	outline: none;
}

.subsida {
	width: 100%;
	flex: 1;
	display: flex;
	flex-direction: column;
}
.compl {
	font-size: 90%;
	color:#e2e2e2;
	display: inline-block;
	margin-top:0.4rem;
}
.title {
	margin-top:1.5rem;
}

.spalt {
	flex: 1;
	align-items: stretch;
}
.content {
	padding:2rem 0;
	background-color: white;
}
.intro {
	padding:2rem;
}

.form-container {
	display: grid;
	gap: 1.5rem;
	margin: 1rem 0;
	padding: 1.5rem 1.5rem 1.8rem 1.5rem;
	background: tint($color-black, 98%);
	border:1px solid tint($color-black, 75%);
}
.company {
    background: #fff;
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: .4rem 0;
    position: relative;
    gap: 1rem;
	border-bottom:4px solid $color-bv-logo-blue;
	border-top:4px solid $color-bv-logo-blue;
}
.text-black {
	color:#000;
}

.company_info {
    width: 100%;
	align-self: center;
}
.company_info_name {
	font-size: 1.2rem;
	font-weight: bold;
	margin-bottom: 0;
}
.company_info_number {
	font-size: 1rem;
	margin-bottom: 0;
}

.icon_img {
    margin-right: 1em;
    margin-top: 1em;
    margin-bottom: 1em;
    height: 4rem;
}
/* Kvittens */

.kvittens-kvitto {
  border-style: solid;
  border-color: #69b554;
  padding: 2em;
  margin-top: 1em;
  margin-bottom: auto;
  margin-right: auto;
  margin-left: auto;
  flex: 1;
  display: flex;
}

.icon-left {
  margin-right: 1.5em;
}

.check-icon {
  width: 3.2em;
}

.kvittens-label {
  flex-direction: column;
}

.kvittens-value {
  margin-left: 3em;
}

.div-positioner {
  position: relative;
}

.text strong {
	color: inherit;
	font-weight:bold;
}

.kvittens-container {
  display: flex;
  margin-top: 1em;
  flex-direction: column;
}

.kvittens-container p {
	margin: 1rem 0 0 0;
}

.kvittens-container dl {
	display: grid;
	grid-template: auto / 135px 1fr;
	grid-auto-columns: minmax(200px, auto);
	gap: 0.5rem;
}
.kvittens-container dt, 
.kvittens-container dd {
	margin: 0;
}
.kvittens-container dt {
	background-color: inherit;
}
.kvittens-container dd {
	background-color: inherit;
}


@media screen and (max-width: 35em) {
	.kvittens-container dl {
		display: grid;
		grid-template-columns: repeat(1, 1fr);
		row-gap: 0.5rem;
	}
	.kvittens-container dd {
		margin-bottom: 0.5rem
	}
}
</style>